import React, { useState, useEffect, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import styled from 'styled-components';
import { Question, QuestionSelectBase, Serializer } from 'survey-core';
import { ReactElementFactory, Survey } from 'survey-react-ui';
import chartStyles from '../charts/css/TransformationSlider.module.css';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper as SwiperType } from 'swiper/types';
import { useTranslation } from 'react-i18next';

interface CarouselChoice {
  value: string;
  text: string;
  imageUrl?: string;
  selectedImageUrl?: string;
  description?: string;
}

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const ChoiceCard = styled.div`
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0px 3.09px 6.19px 0px #00000014;

  &.selected {
    border-color: #007bff;
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
  }
`;

const ChoiceImage = styled.img`
  object-fit: cover;
  border-radius: 10px;
`;

const ChoiceText = styled.div`
  font-family: 'Lato', sans-serif;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  text-align: center;
`;

const ChoiceDescription = styled.p`
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  margin: 0;
  font-size: 12px;
  color: #666;
  text-align: center;
  line-height: 1.4;
`;

const ImageContainer = styled.div`
  border-radius: 8px;
  position: relative;
`;

const SelectedWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #0B6DFF4D;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SelectedText = styled.div`
  font-weight: 800;
  color: #0B6DFF;
  background-color: #fff;
  border-radius: 100px;
  padding: 8px 12px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  font-family: 'Oswald', sans-serif;
  font-size: 16px;

  span {
    padding-right: 8px;
  }
`;

const CarouselQuestion: React.FC<Question> = (props) => {
  const { t } = useTranslation();
  const question = props.element;
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    question.value || null
  );
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null);

  useEffect(() => {
    setSelectedValue(question.value || null);
  }, [question]);

  const choices: CarouselChoice[] = question.visibleChoices || [];

  const handleChoiceSelect = (value: string) => {
    question.value = value;
    setSelectedValue(value);
  };

  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper.realIndex);
  };

  const titleHtml = question.locTitle.renderedHtml || question.title || "";
  const descriptionHtml = question.locDescription.renderedHtml || question.description || "";

  const handleNext = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (swiperRef) {
      swiperRef.slideNext();
    }
  }, [swiperRef]);

  const handlePrev = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (swiperRef) {
      swiperRef.slidePrev();
    }
  }, [swiperRef]);

  return (
    <CarouselContainer>
      {titleHtml && (
        <h5 className="sd-title sd-element__title sd-question__title sd-question__title--required sd-question__title--answer">
          <span dangerouslySetInnerHTML={{ __html: titleHtml }} className="sv-string-viewer" />
        </h5>
      )}
      {descriptionHtml && (
        <div className='sd-description sd-question__description' style={{marginBottom: '24px'}}>
          <span dangerouslySetInnerHTML={{ __html: descriptionHtml }} className="sv-string-viewer" />
        </div>
      )}

      {/* {activeIndex === 0 ? <button className={`${chartStyles.navButtonNone} ${chartStyles.navPrev}`} ></button> : (
        <button className={`${chartStyles.navButton} ${chartStyles.navPrev}`} style={{left: '40px'}}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )} */}
      {question.errors?.length > 0 && (
        <div className="sd-question__erbox sd-question__erbox--outside-question sd-question__erbox--above-question">
          <span className="sv-string-viewer">{t("quiz.carousel.responseRequired")}</span>
        </div>
      )}

      <Swiper
        modules={[Navigation]}
        spaceBetween={10}
        slidesPerView={1.3}
        onSlideChange={handleSlideChange}
        centeredSlides={true}
        onSwiper={(swiper: SwiperType) => setSwiperRef(swiper)}
        className='carousel-swiper'
      >
        {choices.map((choice, index) => (
          <SwiperSlide key={index}>
            <ChoiceCard
              className={selectedValue === choice.value ? 'selected' : ''}
              onClick={() => handleChoiceSelect(choice.value)}
            >
              {choice.imageUrl && (
                <ImageContainer>
                  <ChoiceImage src={choice.imageUrl} alt={choice.text} />
                  {selectedValue === choice.value && (
                    <SelectedWrapper>
                      <SelectedText>
                        <span>{t("quiz.carousel.selected")}</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="20" height="20" rx="10" fill="#0B6DFF"/>
                          <path d="M8.35703 14.5002L4.08203 10.2252L5.15078 9.15645L8.35703 12.3627L15.2383 5.48145L16.307 6.5502L8.35703 14.5002Z" fill="white"/>
                        </svg>
                      </SelectedText>
                    </SelectedWrapper>
                  )}
                  {activeIndex === index && activeIndex > 0 && (
                    <button className={`${chartStyles.navButton} ${chartStyles.navPrev}`} style={{left: '-45px'}} onClick={handlePrev}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </button>
                  )}
                  {activeIndex === index && activeIndex < choices.length - 1 && (
                    <button className={`${chartStyles.navButton} ${chartStyles.navNext}`} style={{right: '-45px'}} onClick={handleNext}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </button>
                  )}
                </ImageContainer>
              )}

              <div>
                <ChoiceText>{choice.text}</ChoiceText>
                {choice.description && (
                  <ChoiceDescription>{choice.description}</ChoiceDescription>
                )}
              </div>
            </ChoiceCard>
          </SwiperSlide>
        ))}
      </Swiper>
      {/* { activeIndex === choices.length - 1 ?   <button className={`${chartStyles.navButtonNone} ${chartStyles.navNext}`} ></button>: 
        <button className={`${chartStyles.navButton} ${chartStyles.navNext}`} style={{right: '40px'}}>
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        </button>
      } */}
    </CarouselContainer>
  );
};

export class CustomCarouselQuestion extends QuestionSelectBase {
    getType() {
      return "carousel";
    }

    getChoices() {
      return this.choices;
    }


  }
  
Serializer.addClass(
    "carousel",
    [{ name: "choices:text", default: [] }],
    () => new CustomCarouselQuestion(""),
    "selectbase"
  );

ReactElementFactory.Instance.registerElement('carousel', (props) => {
  return React.createElement(CarouselQuestion, props);
});

Serializer.addProperty("itemvalue", {
  name: "imageUrl:string",
  category: "general"
});
Serializer.addProperty("itemvalue", {
  name: "description:text",
  category: "general",
  isLocalizable: true,
});

export default CarouselQuestion;
